import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNoteSticky, faTimes, faRobot, faUser, faEdit, faTrash, faPlus, faSave } from '@fortawesome/free-solid-svg-icons';
import BtnTaratic from '../ui/BtnTaratic';
import { createPortal } from 'react-dom';
import chatService, { type ChatNote } from '../../services/chatService';
import { useNotifications } from '../ui/notifications';
import { useDeleteConfirmation } from '../ui/DeleteConfirmationProvider';

interface ThreadNotesPanelProps {
  threadId: string;
}

export const ThreadNotesPanel: React.FC<ThreadNotesPanelProps> = ({ threadId }) => {
  const notifications = useNotifications();
  const confirmDelete = useDeleteConfirmation();
  const [isOpen, setIsOpen] = useState(false);
  const [notes, setNotes] = useState<ChatNote[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Create / Edit state
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const data = await chatService.getNotes(threadId);
      setNotes(data || []);
    } catch (e) {
      console.error("Failed to fetch notes", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchNotes();
      setEditingNoteId(null);
      setNoteContent('');
    }
  }, [isOpen, threadId]);

  const handleSave = async () => {
    if (!noteContent.trim()) {
      notifications.warning('Revisa la nota', 'Escribe algún contenido antes de guardarla.');
      return;
    }
    
    try {
      setIsSaving(true);
      if (editingNoteId) {
        await chatService.updateNote(editingNoteId, noteContent);
      } else {
        await chatService.createNote(threadId, noteContent);
      }
      notifications.success(editingNoteId ? 'Nota actualizada' : 'Nota creada', 'La nota se guardó correctamente.');
      setEditingNoteId(null);
      setNoteContent('');
      await fetchNotes();
    } catch (e) {
      console.error("Failed to save note", e);
      notifications.error('No se pudo guardar la nota', 'Inténtalo de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    if (!await confirmDelete({ title: 'Eliminar nota', message: '¿Seguro que deseas eliminar esta nota?' })) return;
    try {
      await chatService.deleteNote(noteId);
      await fetchNotes();
    } catch (e) {
      console.error("Failed to delete note", e);
      alert('Error al eliminar nota.');
    }
  };

  return (
    <>
      <BtnTaratic 
        color="ghost-dark"
        onClick={() => setIsOpen(true)}
        className="px-3 py-1.5 flex items-center gap-2"
        title="Notas del Hilo"
      >
        <FontAwesomeIcon icon={faNoteSticky} className="icon-inner-glow-white text-yellow-400" />
        <span className="text-sm font-medium">Notas</span>
      </BtnTaratic>

      {isOpen && createPortal(
        <div data-ui="chat-utility-modal-overlay" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div 
            ref={panelRef}
            data-ui="chat-utility-modal"
            className="chat-utility-modal bg-slate-950/95 text-white backdrop-blur-xl border border-white/15 rounded-3xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden"
            style={{ maxHeight: '85vh', height: '85vh' }}
          >
            <div data-ui="chat-utility-modal-header" className="px-6 py-5 border-b border-white/10 flex justify-between items-center bg-gradient-to-r from-amber-950/60 to-slate-950">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <FontAwesomeIcon icon={faNoteSticky} className="text-yellow-400" />
                  Notas del Chat
                </h2>
                <p className="text-xs text-slate-400 mt-1">Guarda contexto interno para que el equipo pueda continuar la conversación.</p>
              </div>
              <BtnTaratic
                color="ghost-dark"
                iconOnly
                onClick={() => setIsOpen(false)}
                data-ui="chat-utility-modal-close"
                className="transition-colors"
                aria-label="Cerrar notas"
              >
                <FontAwesomeIcon icon={faTimes} />
              </BtnTaratic>
            </div>

            {/* Note Editor Area (Top) */}
            <div data-ui="chat-note-editor" className="p-5 border-b border-white/10 bg-slate-900/65">
              <textarea
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none min-h-[92px]"
                placeholder={editingNoteId ? "Editando nota..." : "Escribe una nueva nota sobre este paciente..."}
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
              />
              <div className="flex justify-end gap-2 mt-2">
                {editingNoteId && (
                  <BtnTaratic color="ghost-dark" onClick={() => {
                    setEditingNoteId(null);
                    setNoteContent('');
                  }}>
                    Cancelar
                  </BtnTaratic>
                )}
                <BtnTaratic 
                  color={editingNoteId ? 'green' : 'blue'}
                  onClick={handleSave}
                  disabled={isSaving || !noteContent.trim()}
                  className="disabled:cursor-not-allowed disabled:pointer-events-auto"
                >
                  <FontAwesomeIcon icon={editingNoteId ? faSave : faPlus} className="mr-2" />
                  {editingNoteId ? 'Actualizar' : 'Añadir'}
                </BtnTaratic>
              </div>
            </div>

            {/* List of Notes */}
            <div data-ui="chat-utility-modal-content" className="flex-1 overflow-y-auto p-4 space-y-3">
              {loading && notes.length === 0 ? (
                <div className="text-center text-white/50 text-sm">Cargando notas...</div>
              ) : notes.length === 0 ? (
                <div className="text-center text-white/50 text-sm py-8 flex flex-col items-center">
                  <FontAwesomeIcon icon={faNoteSticky} className="text-4xl text-white/10 mb-3" />
                  No hay notas para esta conversación.
                </div>
              ) : (
                notes.map(n => {
                  const isHuman = n.author_type === 'human';
                  const isEditing = editingNoteId === n.id;
                  return (
                    <div
                      data-ui="chat-note-card"
                      data-note-author={isHuman ? 'operator' : 'ai'}
                      key={n.id} 
                      className={`relative overflow-hidden rounded-2xl p-4 border transition-colors ${
                        isHuman ? 'bg-blue-500/10 border-blue-400/25' : 'bg-purple-500/10 border-purple-400/25'
                      } ${isEditing ? 'ring-1 ring-white/30' : ''}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2 text-xs font-medium">
                          {isHuman ? (
                            <span className="text-blue-400 flex items-center gap-1.5"><FontAwesomeIcon icon={faUser}/> Operador</span>
                          ) : (
                            <span className="text-purple-400 flex items-center gap-1.5"><FontAwesomeIcon icon={faRobot}/> IA Memory</span>
                          )}
                          <span data-ui="chat-note-timestamp" className="text-white/30">• {new Date(n.created_at).toLocaleString()}</span>
                        </div>
                        
                        {isHuman && (
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => {
                                setEditingNoteId(n.id);
                                setNoteContent(n.content);
                              }}
                              className="text-white/40 hover:text-blue-400 p-1.5 transition-colors"
                              title="Editar"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button 
                              onClick={() => handleDelete(n.id)}
                              className="text-white/40 hover:text-red-400 p-1.5 transition-colors"
                              title="Eliminar"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div data-ui="chat-note-content" className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">
                        {isEditing ? noteContent : n.content}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
